import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { KEYS } from '../../../constants/keys.js';
import { canDraggedNodeBeDroppedOnBottomBar, isTreeDropNodeValid, canDraggedNodeBeDroppedOnTopBar } from '../utils.js';

var useTreeA11y = function useTreeA11y(props) {
  var _React$useState = React.useState(),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    focusedNodeId = _React$useState2[0],
    setFocusedNodeId = _React$useState2[1];
  var _React$useState3 = React.useState(),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    focusedDropRegion = _React$useState4[0],
    setFocusedDropRegion = _React$useState4[1];
  var focusOnNode = function focusOnNode(currentAbsoluteIndex) {
    props.onFocusIndexChange(currentAbsoluteIndex);
  };
  var focusOnNextNode = function focusOnNextNode(currentAbsoluteIndex) {
    props.onFocusIndexChange(props.context.getNextVisibleAbsoluteIndex(currentAbsoluteIndex));
  };
  var focusOnPreviousNode = function focusOnPreviousNode(currentAbsoluteIndex) {
    props.onFocusIndexChange(props.context.getPrevVisibleAbsoluteIndex(currentAbsoluteIndex));
  };
  var isValidDropRegion = function isValidDropRegion(draggedNodeContext, targetNodeContext, dropRegion) {
    switch (dropRegion) {
      case "top":
        return canDraggedNodeBeDroppedOnTopBar(draggedNodeContext, targetNodeContext, props.canDrop);
      case "over":
        var targetNode = props.context.nodeById[targetNodeContext.id];
        return isTreeDropNodeValid({
          canDrop: props.canDrop,
          sourceNodeContext: draggedNodeContext,
          target: {
            context: targetNodeContext,
            node: targetNode
          }
        });
      case "bottom":
        return canDraggedNodeBeDroppedOnBottomBar(draggedNodeContext, targetNodeContext, props.canDrop);
    }
  };
  var expandNodeChildren = function expandNodeChildren(nodeContext) {
    var nextHiddenIds = new Set(props.context.hiddenIds);
    nodeContext.getChildIds().forEach(function (childId) {
      return nextHiddenIds.delete(childId);
    });
    if (typeof props.onExpand === "function") {
      props.onExpand({
        context: nodeContext,
        node: props.nodeById[nodeContext.id]
      });
    }
    props.onChangeHiddenIds(nextHiddenIds);
  };
  var collapseNodeChildren = function collapseNodeChildren(nodeContext) {
    var nextHiddenIds = new Set(props.context.hiddenIds);
    nodeContext.getChildIds().forEach(function (childId) {
      return nextHiddenIds.add(childId);
    });
    props.onChangeHiddenIds(nextHiddenIds);
    if (props.onCollapse) {
      props.onCollapse({
        context: nodeContext,
        node: props.context.nodeById[nodeContext.id]
      });
    }
  };
  var focusOnNextDroppableRegion = function focusOnNextDroppableRegion(currentFocusedNodeId, currentFocusedDropRegion) {
    setFocusedNodeId(currentFocusedNodeId);
    setFocusedDropRegion(currentFocusedDropRegion);
    var draggedNodeContext = props.context.getNodeContext(props.a11yDragId);
    var focusedNodeContext = props.context.getNodeContext(currentFocusedNodeId);
    var nextNodeId = props.context.getNextVisibleNodeId(focusedNodeContext.absoluteIndex);
    var nextNodeContext = props.context.getNodeContext(nextNodeId);
    switch (currentFocusedDropRegion) {
      case "top":
        setFocusedDropRegion("over");
        if (isValidDropRegion(draggedNodeContext, focusedNodeContext, "over")) {
          focusOnNode(focusedNodeContext.absoluteIndex);
          return;
        }
        focusOnNextDroppableRegion(focusedNodeContext.id, "over");
        return;
      case "over":
        if (focusedNodeContext.isLastChild() && !focusedNodeContext.isExpanded()) {
          setFocusedDropRegion("bottom");
          if (isValidDropRegion(draggedNodeContext, focusedNodeContext, "bottom")) {
            focusOnNode(focusedNodeContext.absoluteIndex);
            return;
          }
          focusOnNextDroppableRegion(focusedNodeContext.id, "bottom");
          return;
        }
        setFocusedNodeId(nextNodeId);
        setFocusedDropRegion("top");
        if (isValidDropRegion(draggedNodeContext, nextNodeContext, "top")) {
          focusOnNextNode(focusedNodeContext.absoluteIndex);
          return;
        }
        focusOnNextDroppableRegion(nextNodeId, "top");
        return;
      case "bottom":
        if (nextNodeId === focusedNodeContext.id) {
          return;
        }
        setFocusedNodeId(nextNodeId);
        setFocusedDropRegion("top");
        if (isValidDropRegion(draggedNodeContext, nextNodeContext, "top")) {
          focusOnNextNode(focusedNodeContext.absoluteIndex);
          return;
        }
        focusOnNextDroppableRegion(nextNodeId, "top");
        return;
    }
  };
  var focusOnPreviousDroppableRegion = function focusOnPreviousDroppableRegion(currentFocusedNodeId, currentFocusedDropRegion) {
    var draggedNodeContext = props.context.getNodeContext(props.a11yDragId);
    var focusedNodeContext = props.context.getNodeContext(currentFocusedNodeId);
    var previousNodeId = props.context.getPreviousVisibleNodeId(focusedNodeContext.absoluteIndex);
    var previousNodeContext = props.context.getNodeContext(previousNodeId);
    switch (currentFocusedDropRegion) {
      case "bottom":
        setFocusedDropRegion("over");
        if (isValidDropRegion(draggedNodeContext, focusedNodeContext, "over")) {
          focusOnNode(focusedNodeContext.absoluteIndex);
          return;
        }
        focusOnPreviousDroppableRegion(focusedNodeContext.id, "over");
        return;
      case "over":
        setFocusedDropRegion("top");
        if (isValidDropRegion(draggedNodeContext, focusedNodeContext, "top")) {
          focusOnNode(focusedNodeContext.absoluteIndex);
          return;
        }
        focusOnPreviousDroppableRegion(focusedNodeContext.id, "top");
        return;
      case "top":
        if (previousNodeId === focusedNodeContext.id) {
          return;
        }
        setFocusedNodeId(previousNodeId);
        if (previousNodeContext.isLastChild() && !previousNodeContext.isExpanded()) {
          setFocusedDropRegion("bottom");
          if (isValidDropRegion(draggedNodeContext, previousNodeContext, "bottom")) {
            focusOnPreviousNode(focusedNodeContext.absoluteIndex);
            return;
          }
          focusOnPreviousDroppableRegion(previousNodeId, "bottom");
          return;
        }
        setFocusedDropRegion("over");
        if (isValidDropRegion(draggedNodeContext, previousNodeContext, "over")) {
          focusOnPreviousNode(focusedNodeContext.absoluteIndex);
          return;
        }
        focusOnPreviousDroppableRegion(previousNodeId, "over");
        return;
    }
  };
  var startDragging = function startDragging(nodeContext) {
    props.setA11yDragId(nodeContext.id);
    collapseNodeChildren(nodeContext);
    setFocusedNodeId(nodeContext.id);
    setFocusedDropRegion("over");
    if (props.onDragStart) {
      props.onDragStart({
        level: nodeContext.getAriaLevel()
      });
    }
  };
  var finishDragging = function finishDragging(targetNodeContext) {
    if (props.a11yDragId === targetNodeContext.id) {
      return;
    }
    var position = "childof";
    switch (focusedDropRegion) {
      case "over":
        position = "childof";
        break;
      case "top":
        position = "before";
        break;
      case "bottom":
        position = "after";
        break;
    }
    var source = {
      context: props.context.getNodeContext(props.a11yDragId),
      node: props.context.nodeById[props.a11yDragId]
    };
    var target = {
      context: targetNodeContext,
      node: props.context.nodeById[focusedNodeId]
    };
    props.onChange({
      position: position,
      source: source,
      target: target
    });
    var nextHiddenIds = new Set(props.hiddenIds);
    if (targetNodeContext.isExpanded() || !targetNodeContext.hasChildren()) {
      nextHiddenIds.delete(source.context.id);
    } else {
      nextHiddenIds.add(source.context.id);
    }
    props.setHiddenIds(nextHiddenIds);
    props.setA11yDragId(void 0);
  };
  var onEscapeKey = function onEscapeKey() {
    if (!props.a11yDragId) {
      return;
    }
    var draggedNodeContext = props.context.getNodeContext(props.a11yDragId);
    props.onFocusIndexChange(draggedNodeContext.absoluteIndex);
    props.setA11yDragId(void 0);
    setFocusedNodeId(void 0);
    setFocusedDropRegion(void 0);
  };
  var onSpaceKey = function onSpaceKey(event, nodeContext) {
    var _props$variant;
    var currentNodeContext;
    if (props.a11yDragId && focusedNodeId) {
      currentNodeContext = props.context.getNodeContext(focusedNodeId);
      setFocusedNodeId(void 0);
      setFocusedDropRegion(void 0);
      finishDragging(currentNodeContext);
      props.setA11yDragId(void 0);
    } else {
      currentNodeContext = props.context.getNodeContext(nodeContext.id);
      if (props.onChange !== void 0 && props.canDrag({
        context: currentNodeContext,
        node: props.context.nodeById[currentNodeContext.id]
      })) {
        startDragging(currentNodeContext);
      }
    }
    var options = {
      context: currentNodeContext,
      event: event,
      node: props.context.nodeById[currentNodeContext.id]
    };
    switch ((_props$variant = props.variant) === null || _props$variant === void 0 ? void 0 : _props$variant.type) {
      case "multiselect":
        props.variant.onDefaultAction(options);
        break;
      case "select":
        props.variant.onSelect(options);
        break;
    }
  };
  var onArrowLeftKey = function onArrowLeftKey(nodeContext) {
    if (!nodeContext.isExpanded()) {
      focusOnPreviousNode(nodeContext.absoluteIndex);
      return;
    }
    collapseNodeChildren(nodeContext);
  };
  var onArrowRightKey = function onArrowRightKey(nodeContext) {
    if (!nodeContext.hasChildren()) {
      return;
    }
    if (nodeContext.isExpanded()) {
      focusOnNextNode(nodeContext.absoluteIndex);
      return;
    }
    expandNodeChildren(nodeContext);
  };
  var onArrowDownKey = function onArrowDownKey(nodeContext) {
    if (!props.a11yDragId) {
      focusOnNextNode(nodeContext.absoluteIndex);
      return;
    }
    focusOnNextDroppableRegion(focusedNodeId, focusedDropRegion);
  };
  var onArrowUpKey = function onArrowUpKey(nodeContext) {
    if (!props.a11yDragId) {
      focusOnPreviousNode(nodeContext.absoluteIndex);
      return;
    }
    focusOnPreviousDroppableRegion(focusedNodeId, focusedDropRegion);
  };
  var handleKeyDown = function handleKeyDown(event, nodeId) {
    if (event.key === KEYS.TAB && !props.a11yDragId) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    var nodeContext = props.context.getNodeContext(nodeId);
    switch (event.key) {
      case KEYS.ESCAPE:
        onEscapeKey();
        return;
      case KEYS.ENTER:
      case KEYS.SPACE:
        onSpaceKey(event, nodeContext);
        return;
      case KEYS.ARROW_DOWN:
      case KEYS.PAGE_DOWN:
        onArrowDownKey(nodeContext);
        return;
      case KEYS.END:
        props.onFocusIndexChange(props.context.getLastVisibleIndex());
        return;
      case KEYS.HOME:
        props.onFocusIndexChange(0);
        return;
      case KEYS.ARROW_LEFT:
        onArrowLeftKey(nodeContext);
        return;
      case KEYS.ARROW_RIGHT:
        onArrowRightKey(nodeContext);
        return;
      case KEYS.ARROW_UP:
      case KEYS.PAGE_UP:
        onArrowUpKey(nodeContext);
        return;
    }
  };
  return {
    focusedDropRegion: focusedDropRegion,
    focusedNodeId: focusedNodeId,
    handleKeyDown: handleKeyDown
  };
};

export { useTreeA11y };
